const enginePromises = new Map();

function loadPhysicsEngine(url) {
  if (window.Matter) return Promise.resolve();
  if (!enginePromises.has(url)) {
    enginePromises.set(url, new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', () => { enginePromises.delete(url); script.remove(); reject(new Error('Physics unavailable')); }, { once: true });
      document.head.append(script);
    }));
  }
  return enginePromises.get(url);
}

async function loadPlushTexture(config) {
  if (!config?.url) throw new Error('Missing image');
  const image = new Image();
  image.decoding = 'async';
  image.src = config.url;
  await image.decode();
  const crop = config.crop || [0, 0, 100, 100];
  const x = Math.max(0, Math.min(.95, Number(crop[0]) / 100)) * image.naturalWidth;
  const y = Math.max(0, Math.min(.95, Number(crop[1]) / 100)) * image.naturalHeight;
  const width = Math.max(1, Math.min(image.naturalWidth - x, Number(crop[2]) / 100 * image.naturalWidth));
  const height = Math.max(1, Math.min(image.naturalHeight - y, Number(crop[3]) / 100 * image.naturalHeight));
  const scale = Math.min(1, 680 / height);
  const texture = document.createElement('canvas');
  texture.width = Math.max(1, Math.round(width * scale));
  texture.height = Math.max(1, Math.round(height * scale));
  texture.getContext('2d').drawImage(image, x, y, width, height, 0, 0, texture.width, texture.height);
  texture.naturalWidth = texture.width;
  texture.naturalHeight = texture.height;
  const anchor = Number(config.attachX ?? 50) / 100 * image.naturalWidth;
  const clear = Number(config.clearWidth ?? 15) / 200 * image.naturalWidth;
  return {
    ...config,
    image: texture,
    anchorX: (anchor - x) * scale,
    anchorY: Math.max(2, Math.min(texture.height - 2, (Number(config.attachY ?? 30) / 100 * image.naturalHeight - y) * scale)),
    hardwareLeft: Math.max(0, (anchor - clear - x) * scale),
    hardwareRight: Math.min(texture.width, (anchor + clear - x) * scale)
  };
}

class YumiMiniPlushies extends HTMLElement {
  connectedCallback() {
    if (this.lifecycle) return;
    this.lifecycle = new AbortController();
    this.generation = (this.generation || 0) + 1;
    const { signal } = this.lifecycle;
    this.nearby = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { this.nearby.disconnect(); this.boot(); }
    }, { rootMargin: '300px' });
    this.nearby.observe(this);
    document.addEventListener('shopify:section:load', event => {
      if (event.detail?.sectionId === this.dataset.sectionId && !this.loading) this.restart();
    }, { signal });
    document.addEventListener('shopify:block:select', event => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.api?.selectBlock(event.detail.blockId);
    }, { signal });
    this.addEventListener('mini-plushies:replay', () => this.api?.replay(), { signal });
  }

  async boot() {
    if (this.loading || this.api || !this.isConnected) return;
    let config;
    try { config = JSON.parse(this.querySelector('[data-config]').textContent); } catch { return; }
    if (!config.animation) return;
    if (config.reducedMotion && matchMedia('(prefers-reduced-motion: reduce)').matches && !config.designMode) return;
    this.loading = true;
    const generation = this.generation;
    try {
      await loadPhysicsEngine(config.engineUrl);
      if (!this.isConnected || generation !== this.generation) return;
      this.api = createMiniPlushies(this, config);
    } catch {
      this.querySelector('[data-state]').textContent = config.errorLabel;
    } finally {
      if (generation === this.generation) this.loading = false;
    }
  }

  restart() {
    this.generation += 1;
    this.api?.destroy();
    this.api = null;
    this.loading = false;
    this.boot();
  }

  disconnectedCallback() {
    this.generation += 1;
    this.nearby?.disconnect();
    this.lifecycle?.abort();
    this.lifecycle = null;
    this.api?.destroy();
    this.api = null;
    this.loading = false;
  }
}

if (!customElements.get('yumi-mini-plushies')) customElements.define('yumi-mini-plushies', YumiMiniPlushies);

function createMiniPlushies(root, config) {
  const section=root.querySelector('[data-stage]'),canvas=root.querySelector('[data-canvas]');
  const ctx=canvas.getContext('2d'),hanger=root.querySelector('[data-hanger]');
  const hint=root.querySelector('[data-hint]'),state=root.querySelector('[data-state]');
  const size={textContent:''};
  const {Engine,Bodies,Body,Composite,Constraint,Query,Vector,Sleeping,Events}=window.Matter;
  const engine=Engine.create({enableSleeping:false,positionIterations:10,velocityIterations:8,constraintIterations:10});
  engine.gravity.y=Number(config.gravity)||.9;
  const STEP=1000/120;
  let sprite=null,visuals=[],weightedVisuals=[],hangerVisual=null;
  let COUNT=0;
  const reducedMotion={matches:false};
  let width=0,height=0,ready=false,visible=false,started=false,disposed=false;
  let hanging=null,dolls=[],spawned=0,spawnClock=0,accumulator=0,lastTime=0,raf=0;
  let drag=null,dragged=null,pointerId=null,anchor=null,pointerStart=null;
  let scrollGoal=0,scrollVelocity=0,scrollAcceleration=0,lastInput=0,lastRealScroll=0;
  let lastScrollY=scrollPosition(),lastWheelTime=0,lastScrollTime=0,touchY=null;
  let scrollSource='none',scrollEvents=0,frameCount=0,scrollCost=0,scrollSamples=0;
  let demoTimer=null,selectedBlock=null;
  const abort=new AbortController();
  const listen=(node,type,fn,options={})=>node?.addEventListener(type,fn,{...options,signal:abort.signal});
  const mobile=()=>matchMedia('(max-width:749px)').matches;
  const referenceHeight=()=>Number(mobile()?config.sizeMobile:config.sizeDesktop)||98;
  const anchorPosition=()=>width*(Number(config.anchorX)||50)/100;
  let intensity=config.scroll?Number(config.strength)||0:0;
  const cut1=Math.max(.05,Math.min(.25,Number(config.splits[0])/100));
  const cut2=Math.max(cut1+.02,Math.min(.4,Number(config.splits[1])/100));
  const cut3=Math.max(cut2+.02,Math.min(.5,Number(config.splits[2])/100));
  const cuts=[0,cut1,cut2,cut3,1];
  const swayEnabled=config.swayEnabled!==false&&Number(config.swayAmplitude??3)>0;
  let swayPhase=0,lastSwayInteraction=-Infinity;
  function applySway(){
    if(!hanging||!swayEnabled)return;
    wakeDoll(hanging);
    if(dragged?.plugin.doll===hanging)return;
    swayPhase+=STEP/(Math.max(3,Number(config.swayPeriod)||4.5)*1000)*Math.PI*2;
    const recovery=clamp((performance.now()-lastSwayInteraction-650)/1400,0,1);
    const amplitude=clamp(Number(config.swayAmplitude??3),0,8)*Math.PI/180;
    const acceleration=Math.sin(swayPhase)*Math.tan(amplitude)*engine.gravity.y*.001*.6*recovery;
    hanging.bodies.forEach(body=>Body.applyForce(body,body.position,{x:body.mass*acceleration,y:0}));
  }
  const allDolls=()=>hanging?[...dolls,hanging]:dolls;
  const allBodies=()=>allDolls().flatMap(d=>d.bodies);
  function wakeDoll(doll) {
    if(!doll)return;
    if(doll.asleep){
      Composite.add(engine.world,doll.joints);
      if(doll.isHanging&&anchor)Composite.add(engine.world,anchor);
      doll.asleep=false;doll.previousPose=null;
    }
    doll.rest=0;doll.quietPose=null;doll.quietSteps=0;doll.bodies.forEach(b=>Sleeping.set(b,false));
  }
  function sleepDoll(doll){
    if(doll.asleep)return;


    doll.joints.forEach(c=>Composite.remove(engine.world,c));
    if(doll.isHanging&&anchor)Composite.remove(engine.world,anchor);
    doll.bodies.forEach(b=>{
      b.constraintImpulse.x=0;b.constraintImpulse.y=0;b.constraintImpulse.angle=0;
      b.force.x=0;b.force.y=0;b.torque=0;
      Body.setVelocity(b,{x:0,y:0});Body.setAngularVelocity(b,0);Sleeping.set(b,true);
    });
    doll.asleep=true;
  }
  const clamp=(v,min,max)=>Math.max(min,Math.min(max,v));
  function resetScroll(){scrollGoal=0;scrollVelocity=0;scrollAcceleration=0;lastInput=0;lastWheelTime=0;lastScrollTime=0;lastScrollY=scrollPosition();}
  function feedScroll(delta,dt,source){
    if(!visible||!ready||document.hidden||reducedMotion.matches||drag||!intensity||Math.abs(delta)<.5)return;
    const velocity=clamp(delta*1000/clamp(dt,16,80),-2200,2200);
    scrollGoal=scrollGoal*.25+velocity*.75;
    lastInput=performance.now();lastSwayInteraction=lastInput;scrollSource=source;scrollEvents++;
    allDolls().forEach(wakeDoll);root.dataset.settled='false';wake();
  }
  function scrollPosition(){return -section.getBoundingClientRect().top;}
  function onScroll(){
    const now=performance.now(),y=scrollPosition(),delta=y-lastScrollY;
    lastScrollY=y;
    if(Math.abs(delta)>650){resetScroll();return;}
    lastRealScroll=now;feedScroll(delta,lastScrollTime?now-lastScrollTime:32,'page');lastScrollTime=now;
  }
  listen(window,'scroll',onScroll,{passive:true,capture:true});
  function sampleScroll(time,elapsed){
    if(reducedMotion.matches||!intensity){resetScroll();return;}
    const goal=time-lastInput<75?scrollGoal:0;
    const next=scrollVelocity+(goal-scrollVelocity)*(1-Math.exp(-elapsed/65));


    scrollAcceleration=clamp((next-scrollVelocity)/(elapsed/1000)*.00000035,-.0035,.0035)*intensity/100;
    scrollVelocity=Math.abs(next)<.5&&goal===0?0:next;
  }
  function applyScroll(){
    if(Math.abs(scrollAcceleration)<.000006)return;
    const t=performance.now();
    allDolls().forEach(d=>{
      wakeDoll(d);
      d.bodies.forEach(b=>{


        const offset=d.h*.015*(d.isHanging?1:(d.index%2?1:-1));
        Body.applyForce(b,{x:b.position.x+offset,y:b.position.y},{x:0,y:b.mass*scrollAcceleration});
      });
    });
    scrollCost+=performance.now()-t;scrollSamples++;
  }
  function joint(a,b,x,y) {
    return Constraint.create({bodyA:a,pointA:{x:x-a.position.x,y:y-a.position.y},bodyB:b,pointB:{x:x-b.position.x,y:y-b.position.y},length:0,stiffness:0.98,damping:0.06});
  }
  function makeDoll(x,top,h,isHanging,index) {
    const w=h*sprite.naturalWidth/sprite.naturalHeight;
    const group=Body.nextGroup(true);
    const filter={group,category:isHanging?2:4,mask:isHanging?1:5};
    const bodies=[], joints=[];
    for(let i=0;i<4;i++) {
      const a=cuts[i],b=cuts[i+1],cy=top+(a+b)*h/2;
      const opts={collisionFilter:filter,friction:(Number(config.friction)/100),frictionStatic:.95,restitution:i===3?(Number(config.bounce)/100):Math.min(.1,(Number(config.bounce)/100)/3),frictionAir:isHanging?0.012:0.018,sleepThreshold:Infinity};
      let body;
      if(i<3) body=Bodies.rectangle(x,cy,w*[0.19,0.09,0.10][i],(b-a)*h*.94,{...opts,chamfer:{radius:Math.min(2,(b-a)*h*.15)}});
      else body=Body.create({...opts,parts:[
        Bodies.circle(x,top+h*(cut3+(1-cut3)*.25),h*.203),
        Bodies.rectangle(x,top+h*(cut3+(1-cut3)*.678),w*.53,h*.42,{chamfer:{radius:h*.06}}),
        Bodies.rectangle(x,top+h*(cut3+(1-cut3)*.552),w*.94,h*.12,{chamfer:{radius:h*.04}})
      ]});
      Body.setMass(body,[0.20,0.12,0.12,1.6][i]*(h/170)**2);
      const overlap=i===3?0.0015:0.003;
      const sa=Math.max(0,a-overlap),sb=Math.min(1,b+overlap);
      body.plugin={art:{sy:sa*sprite.naturalHeight,sh:(sb-sa)*sprite.naturalHeight,w,h:(sb-sa)*h,ox:x-body.position.x,oy:top+(sa+sb)*h/2-body.position.y},part:i};
      body.plugin.visualOrigin={x:x-body.position.x,y:top-body.position.y};
      bodies.push(body);
      if(i>0)joints.push(joint(bodies[i-1],body,x,top+a*h));
    }
    const doll={bodies,joints,h,w,rest:0,index,isHanging,asleep:false,hasLanded:false,visual:isHanging?hangerVisual:weightedVisuals[index%weightedVisuals.length]};
    bodies.forEach(b=>{b.plugin.doll=doll;});
    Composite.add(engine.world,[...bodies,...joints]);
    if(isHanging) {
      anchor=Constraint.create({pointA:{x,y:top},bodyB:bodies[0],pointB:{x:x-bodies[0].position.x,y:top-bodies[0].position.y},length:0,stiffness:1,damping:0.08});
      Composite.add(engine.world,anchor);
    } else {
      const angle=Number(config.rotation)/100*[-.38,.24,-.18,.42,-.31,.15,-.47,.29,.08,-.25,.36,-.12,.48,-.34,.2][index%15];
      const spin=Number(config.rotation)/100*[.004,-.003,.006,-.004,.002,-.005,.003,.005,-.002,.004,-.006,.002,-.003,.005,-.004][index%15];
      const drift=Number(config.spread)/100*[.42,-.3,.25,-.48,.3,-.22,.5,-.4,.28,-.34,.24,-.45,.36,-.28,.4][index%15];
      const pivot={x,y:top+h*.65};
      bodies.forEach((b,i)=>{
        Body.rotate(b,angle,pivot);
        Body.setVelocity(b,{x:drift,y:.6});

        Body.setAngularVelocity(b,spin+(i===3?0:(i-1)*.0005));
      });

      joints.forEach(c=>{
        c.pointA=Vector.rotate(c.pointA,angle);c.pointB=Vector.rotate(c.pointB,angle);
        c.angleA=c.bodyA.angle;c.angleB=c.bodyB.angle;
      });
    }
    return doll;
  }
  Events.on(engine,'collisionStart',event=>{
    for(const pair of event.pairs){
      const a=pair.bodyA.parent,b=pair.bodyB.parent;
      for(const [body,other] of [[a,b],[b,a]]){
        const d=body.plugin?.doll;if(!d||other.plugin?.doll===d)continue;
        if(!d.isHanging&&((other.isStatic&&other.position.y>height)||(!other.isStatic&&other.position.y>=body.position.y)))d.hasLanded=true;
        if(d.asleep&&!other.isStatic&&!other.isSleeping&&other.speed>.5)wakeDoll(d);
      }
    }
  });
  function prepareDolls(){
    allDolls().forEach(d=>{
      if(d.asleep&&d.bodies.some(b=>!b.isSleeping))wakeDoll(d);
      if(d.hasLanded&&!d.asleep)d.bodies.forEach((b,i)=>{b.frictionAir=i===3?.025:.06;});
    });
  }
  function clearDrag() {
    if(drag)Composite.remove(engine.world,drag);
    drag=null;dragged=null;pointerId=null;canvas.style.cursor='';
    root.dataset.dragging='false';
  }
  function resetWorld() {
    resetScroll();
    clearDrag();Composite.clear(engine.world,false);Engine.clear(engine);
    dolls=[];spawned=0;spawnClock=0;accumulator=0;
    const options={isStatic:true,friction:(Number(config.friction)/100),restitution:Math.min(.1,(Number(config.bounce)/100)/2),collisionFilter:{category:1,mask:7}};
    Composite.add(engine.world,[Bodies.rectangle(width/2,height+14,width+160,60,options),Bodies.rectangle(-20,height/2-200,64,height+650,options),Bodies.rectangle(width+20,height/2-200,64,height+650,options)]);
    hanging=config.showHanging&&hangerVisual?makeDoll(anchorPosition(),2,Number(mobile()?config.hangingMobile:config.hangingDesktop),true,-1):null;
    hint.style.top=((hanging?.h||0)+19)+'px';
    hanger.disabled=!hanging;
    root.dataset.count='0';root.dataset.settled='false';
  }
  function spawn() {
    const index=spawned,h=referenceHeight();
    const lanes=[.16,.78,.35,.64,.88,.23,.53,.73,.12,.44,.81,.30,.61,.19,.9,.47,.69,.38,.13,.56];
    const x=Math.max(h*.4,Math.min(width-h*.4,width*lanes[index%lanes.length]));
    const top=-h-12;
    dolls.push(makeDoll(x,top,h,false,index));spawned++;
    root.dataset.count=String(spawned);root.dataset.spawnY=String(top);
  }
  function start(explicit=false) {
    if(!ready)return;
    resetWorld();started=true;root.dataset.state='falling';
    state.textContent='';
    draw();wake();
  }
  listen(root.querySelector('[data-replay]'),'click',()=>{visible=true;start(true);});
  function resize(){
    const w=section.clientWidth,h=section.clientHeight;
    if(!w||!h||(w===width&&h===height))return;
    width=w;height=h;
    const dpr=Math.min(devicePixelRatio||1,2);
    canvas.width=Math.round(w*dpr);canvas.height=Math.round(h*dpr);ctx.setTransform(dpr,0,0,dpr,0,0);
    size.textContent=w+' × '+h;
    if(started)start();else resetWorld();draw();
  }
  function draw(){
    ctx.clearRect(0,0,width,height);
    allDolls().forEach(d=>d.bodies.forEach(body=>{
      const a=body.plugin.art;
      ctx.save();ctx.translate(body.position.x,body.position.y);ctx.rotate(body.angle);
      if(body.plugin.part<3){

        ctx.drawImage(sprite,0,a.sy,sprite.naturalWidth,a.sh,a.ox-a.w/2,a.oy-a.h/2,a.w,a.h);
      }else{
        const v=d.visual,im=v.image,o=body.plugin.visualOrigin;
        const scale=(1-cut3)*d.h/Math.max(1,im.naturalHeight-v.anchorY);
        ctx.translate(o.x,o.y+cut3*d.h);ctx.scale(scale,scale);ctx.translate(-v.anchorX,-v.anchorY);


        ctx.beginPath();
        ctx.rect(0,v.anchorY-2,im.naturalWidth,im.naturalHeight-v.anchorY+2);
        ctx.rect(0,0,Math.max(0,v.hardwareLeft),v.anchorY-2);
        ctx.rect(v.hardwareRight,0,im.naturalWidth-v.hardwareRight,v.anchorY-2);
        ctx.clip();ctx.drawImage(im,0,0);
      }
      ctx.restore();
    }));
    if(hanging){
      const b=hanging.bodies[3],a=b.plugin.art;
      hanger.style.width=a.w+'px';hanger.style.height=a.h+'px';
      hanger.style.left=b.position.x+'px';hanger.style.top=b.position.y+'px';
      hanger.style.transform='rotate('+b.angle+'rad) translate('+(a.ox-a.w/2)+'px,'+(a.oy-a.h/2)+'px)';
      root.dataset.angle=b.angle.toFixed(4);
      root.dataset.linkAngles=hanging.bodies.slice(0,3).map(p=>p.angle.toFixed(4)).join(',');
    }
  }
  function stabilizeSleep(){
    allDolls().forEach(d=>{
      if(d.asleep||(d.isHanging&&swayEnabled))return;
      const pose=d.bodies.map(b=>({x:b.position.x,y:b.position.y,a:b.angle}));


      const still=d.previousPose&&pose.every((p,i)=>Math.hypot(p.x-d.previousPose[i].x,p.y-d.previousPose[i].y)+d.h*(i===3?.4:.08)*Math.abs(p.a-d.previousPose[i].a)<(i===3?.085:.2));
      if(!d.quietPose||pose.some((p,i)=>Math.hypot(p.x-d.quietPose[i].x,p.y-d.quietPose[i].y)+d.h*(i===3?.4:.08)*Math.abs(p.a-d.quietPose[i].a)>2)){
        d.quietPose=pose;d.quietSteps=0;
      }else d.quietSteps=(d.quietSteps||0)+1;
      d.previousPose=pose;
      d.rest=still?d.rest+1:0;
      if((d.rest>150||d.quietSteps>180)&&(d.isHanging||d.hasLanded)&&dragged?.plugin.doll!==d)sleepDoll(d);
    });
  }
  function tick(time){
    raf=0;if(!root.isConnected){cleanup();return;}
    if(!visible||document.hidden||!ready){lastTime=0;return;}
    const elapsed=lastTime?clamp(time-lastTime,1,34):16.67;
    sampleScroll(time,elapsed);
    accumulator+=elapsed;lastTime=time;frameCount++;
    while(accumulator>=STEP){
      if(started&&spawned<COUNT){spawnClock+=STEP;if(spawned===0||spawnClock>=Number(config.dropInterval)){spawn();spawnClock=0;}}
      if(dragged)wakeDoll(dragged.plugin.doll);
      applyScroll();applySway();
      prepareDolls();
      Engine.update(engine,STEP);stabilizeSleep();accumulator-=STEP;
    }
    draw();
    const active=(started&&spawned<COUNT)||allBodies().some(b=>!b.isSleeping)||drag||Math.abs(scrollVelocity)>.5||time-lastInput<100;
    if(spawned===COUNT&&dolls.every(d=>d.bodies.every(b=>b.isSleeping))){root.dataset.settled='true';root.dataset.state='settled';}
    if(active)raf=requestAnimationFrame(tick);else lastTime=0;
  }
  function wake(){if(!raf&&!disposed){lastTime=0;raf=requestAnimationFrame(tick);}}
  function localPoint(e){const r=section.getBoundingClientRect();return{x:(e.clientX-r.left)*width/r.width,y:(e.clientY-r.top)*height/r.height};}
  function hitAt(e){return Query.point(allBodies(),localPoint(e)).slice(-1)[0];}
  listen(canvas,'touchstart',e=>{if(config.drag&&e.touches.length===1&&hitAt(e.touches[0]))e.preventDefault();},{passive:false});
  listen(canvas,'pointerdown',e=>{
    if(e.button!==0)return;
    const b=hitAt(e);if(!b)return;
    clearDrag();pointerStart={x:e.clientX,y:e.clientY,time:performance.now(),body:b,distance:0};
    pointerId=e.pointerId;
    if(!config.drag)return;
    dragged=b;lastSwayInteraction=performance.now();wakeDoll(b.plugin.doll);
    const p=localPoint(e);
    drag=Constraint.create({pointA:p,bodyB:b,pointB:Vector.sub(p,b.position),stiffness:.08,damping:.1,length:0});
    Composite.add(engine.world,drag);canvas.setPointerCapture(e.pointerId);canvas.style.cursor='grabbing';
    hint.style.opacity='0';root.dataset.dragging='true';root.dataset.settled='false';
    e.preventDefault();wake();
  });
  listen(canvas,'pointermove',e=>{
    if(pointerStart)pointerStart.distance=Math.max(pointerStart.distance,Math.hypot(e.clientX-pointerStart.x,e.clientY-pointerStart.y));
    if(drag&&e.pointerId===pointerId){
      const p=localPoint(e);
      if(dragged.plugin.doll.isHanging){
        const dx=p.x-anchorPosition(),dy=p.y-2,r=Math.hypot(dx,dy),limit=hanging.h*.95;
        if(r>limit){p.x=anchorPosition()+dx/r*limit;p.y=2+dy/r*limit;}
      }
      drag.pointA={x:Math.max(12,Math.min(width-12,p.x)),y:Math.max(3,Math.min(height-12,p.y))};
      wakeDoll(dragged.plugin.doll);wake();
    }else if(e.pointerType==='mouse')canvas.style.cursor=hitAt(e)?'grab':'';
  });
  function release(e){
    if(e.pointerId!==pointerId)return;
    const candidate=pointerStart,d=dragged?.plugin.doll;
    pointerStart=null;clearDrag();lastSwayInteraction=performance.now();wakeDoll(d);
    if(canvas.hasPointerCapture(e.pointerId))canvas.releasePointerCapture(e.pointerId);
    if(e.type==='pointerup'&&candidate&&candidate.distance<7&&performance.now()-candidate.time<350&&!config.designMode&&candidate.body.plugin.doll.visual.link)location.assign(candidate.body.plugin.doll.visual.link);
    wake();
  }
  ['pointerup','pointercancel','lostpointercapture'].forEach(name=>listen(canvas,name,release));
  listen(hanger,'keydown',e=>{
    if(!config.drag||!hanging||!['ArrowLeft','ArrowRight',' ','Enter'].includes(e.key))return;
    e.preventDefault();lastSwayInteraction=performance.now();wakeDoll(hanging);const b=hanging.bodies[3];
    Body.setVelocity(b,{x:e.key==='ArrowLeft'?-5:5,y:-1});
    hint.style.opacity='0';wake();
  });

  root.physicsSnapshot=()=>({count:spawned,availableModels:visuals.map(v=>v.id),fallingModels:dolls.map(d=>d.visual.id),hardwareSourceCount:1,restDebug:allDolls().map(d=>({index:d.index,asleep:d.asleep,rest:d.rest,landed:d.hasLanded,speed:Math.max(...d.bodies.map(b=>b.speed)),angular:Math.max(...d.bodies.map(b=>Math.abs(b.angularVelocity)))})),intensity,referenceHeight:referenceHeight(),heights:allDolls().map(d=>d.h),sleepingDolls:allDolls().filter(d=>d.asleep).length,landed:dolls.map(d=>d.hasLanded),active:allBodies().filter(b=>!b.isSleeping).length,visible,frameCount,scroll:{source:scrollSource,events:scrollEvents,velocity:scrollVelocity,acceleration:scrollAcceleration,meanForceMs:scrollSamples?scrollCost/scrollSamples:0},hanging:hanging?.bodies.map(b=>({x:b.position.x,y:b.position.y,angle:b.angle})),falling:dolls.map(d=>d.bodies.map(b=>({x:b.position.x,y:b.position.y,angle:b.angle}))),jointError:Math.max(0,...allDolls().flatMap(d=>d.joints).concat(anchor?[anchor]:[]).map(c=>{const a=Constraint.pointAWorld(c),b=Constraint.pointBWorld(c);return Math.hypot(a.x-b.x,a.y-b.y);})),dragging:!!drag});
  const threshold=clamp(Number(config.trigger)/100,.05,.8);
  const observer=new IntersectionObserver(entries=>{
    const wasVisible=visible;
    visible=entries[0].isIntersecting&&entries[0].intersectionRatio>=threshold;
    if(visible){lastScrollY=scrollPosition();if(ready&&(!started||(!wasVisible&&config.replayOnEnter)))start();wake();}
    else{resetScroll();clearInterval(demoTimer);demoTimer=null;}
  },{threshold:[0,threshold,1]});
  observer.observe(section);
  const resizeObserver=new ResizeObserver(()=>{if(ready)resize();});resizeObserver.observe(section);
  const onVisibility=()=>{if(!document.hidden)wake();};listen(document,'visibilitychange',onVisibility);
  function cleanup(){disposed=true;abort.abort();cancelAnimationFrame(raf);clearInterval(demoTimer);observer.disconnect();resizeObserver.disconnect();Composite.clear(engine.world,false);Engine.clear(engine);root.classList.remove('ymp-ready');delete root.physicsSnapshot;}
  async function prepare(){
    try{
      const master=await loadPlushTexture(config.hardware);
      if(disposed)return;
      sprite=master.image;
      const results=await Promise.allSettled(config.models.map(loadPlushTexture));
      if(disposed)return;
      visuals=results.filter(r=>r.status==='fulfilled').map(r=>r.value);
      weightedVisuals=visuals.flatMap(v=>Array.from({length:clamp(Number(v.frequency)||1,1,5)},()=>v));
      COUNT=weightedVisuals.length?clamp(Number(config.count)||15,1,30):0;
      if(config.showHanging){try{hangerVisual=await loadPlushTexture(config.hanger);}catch{hangerVisual=visuals[0]||null;}}
      if(disposed)return;
      ready=true;resize();root.classList.add('ymp-ready');
      root.querySelector('[data-static]')?.setAttribute('aria-hidden','true');
      if(visible&&!started)start();wake();
    }catch{if(!disposed)state.textContent=config.errorLabel;}
  }
  prepare();
  return {
    destroy:cleanup,
    replay:()=>{if(ready){visible=true;start(true);}},
    selectBlock:id=>{selectedBlock=id;if(ready){visible=true;start(true);}}
  };
}


